import SEO from '../components/SEO';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Calendar, Zap, Shield, TrendingUp, Clock, CheckCircle2, BarChart3, ShoppingCart, Package, Users, Wrench, HeadphonesIcon, Boxes, Factory, Warehouse, TruckIcon, CreditCard, Sparkles, MailOpen, Globe2, Workflow, Layers, Database, Cloud, Award, Target, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackFormSubmission } from '../utils/analytics';
import axios from 'axios';

const AscariiGoLanding = () => {
  const [pricingTier, setPricingTier] = useState('go');
  const [formData, setFormData] = useState({ name: '', email: '', company: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: 'Interested in Ascarii Go',
        source: 'Ascarii Go Landing Page'
      });
      trackFormSubmission('Ascarii Go Form');
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="Ascarii Go | SAP Business One on AWS All-Inclusive Subscription"
        description="All-inclusive SAP B1 subscription on AWS. Implementation, hosting, support, upgrades in one monthly fee. Predictable costs, complete service, no surprises."
        keywords="SAP B1 subscription, cloud ERP subscription, SAP Business One pricing, all-inclusive ERP, Ascarii Go"
        canonical="https://www.ascarii.com/landing/ascarii-go"
      />

      {/* Hero - Enhanced */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6">Ascarii Go × SAP Business One on AWS</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              The ERP solution that starts with you, scales with you -<br />
              <span className="text-sky-300">and runs the way you want to run</span>
            </h1>
            
            <div className="inline-block px-6 py-3 bg-cyan-400/20 backdrop-blur-sm border-2 border-cyan-400/50 rounded-xl mb-8">
              <p className="text-2xl text-cyan-100 font-bold">
                Zero Implementation Fees • Complete Solution • Pure Subscription
              </p>
            </div>

            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-4xl mx-auto">
              Ascarii Go delivers SAP Business One on AWS as a pure subscription. <strong>No implementation fees.</strong> Rapid onboarding and enterprise-grade reliability - so you can focus on outcomes, not overhead.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12 text-left">
              {[
                "Start from £105/user/month - everything included",
                "Free 30-day test environment to help with SAP Upgrades",
                "AWS cloud hosting with 99.995% uptime included",
                "Unlimited support and dedicated account manager"
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <a href="#demo">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-5 text-xl font-semibold border-2 border-white">
                  <Calendar className="w-6 h-6 mr-2" />Book a Demo
                </Button>
              </a>
              <a href="#pricing">
                <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-xl font-semibold">
                  See Pricing
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm text-blue-100">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>SAP Premier Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>AWS Advanced Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>15+ Years Experience</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing - Enhanced */}
      <section className="py-20 bg-white" id="pricing">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 mb-8">User-based plans including SAP Business One licence + all Ascarii Go services.<br />Pure subscription. <strong>No implementation fees.</strong></p>
            
            <div className="inline-flex rounded-2xl border-2 border-blue-300 bg-white p-1.5 shadow-sm">
              <button onClick={() => setPricingTier('go')} className={`px-6 py-3 rounded-xl font-semibold transition focus:outline-none ${pricingTier === 'go' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>Ascarii Go</button>
              <button onClick={() => setPricingTier('go-pro')} className={`px-6 py-3 rounded-xl font-semibold transition focus:outline-none ${pricingTier === 'go-pro' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>Ascarii Go Pro</button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {(pricingTier === 'go' ? [
              { name: 'Starter', price: '£105', users: 'Up to 5 users', licence: 'SAP B1 Starter (excludes Production & MRP)', support: 'Email support' },
              { name: 'Finance/CRM/Logistics', price: '£115', users: 'Any user count', licence: 'Choose Finance, CRM or Logistics per user', support: 'Priority support', popular: true },
              { name: 'Professional', price: '£150', users: 'Any user count', licence: 'Full SAP B1 Professional (incl. Production & MRP)', support: 'Dedicated success manager' }
            ] : [
              { name: 'Starter', price: '£125', users: 'Up to 5 users', licence: 'SAP B1 Starter (excludes Production & MRP)', support: 'Priority support' },
              { name: 'Finance/CRM/Logistics', price: '£135', users: 'Any user count', licence: 'Choose Finance, CRM or Logistics per user', support: 'Priority support', popular: true },
              { name: 'Professional', price: '£170', users: 'Any user count', licence: 'Full SAP B1 Professional (incl. Production & MRP)', support: 'Dedicated success manager' }
            ]).map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className={`bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border-2 ${tier.popular ? 'border-blue-400 shadow-2xl scale-105' : 'border-gray-200 shadow-lg'} relative hover:shadow-xl transition-all`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">Most Popular</div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600"> / user / month</span>
                </div>
                <ul className="space-y-3 mb-6 min-h-[120px]">
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700 text-sm">{tier.users}</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700 text-sm">{tier.licence}</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700 text-sm">{tier.support}</span></li>
                </ul>
                <a href="#included"><Button className="w-full bg-blue-600 hover:bg-blue-700">See Full Features</Button></a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Traditional vs Ascarii Go Comparison */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="compare">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ascarii Go vs Traditional ERP</h2>
            <p className="text-xl text-gray-600">See the difference pure subscription makes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl border border-gray-200 shadow-lg">
              <thead className="bg-gradient-to-r from-blue-600 to-sky-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Aspect</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Traditional ERP</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold bg-blue-700">Ascarii Go</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { aspect: 'Implementation Fees', traditional: '£50k-£100k', go: '£0', highlight: true },
                  { aspect: 'Upfront Investment', traditional: '£70k-£150k', go: '£0', highlight: true },
                  { aspect: 'Cloud Hosting Setup', traditional: '£5k-£10k', go: 'FREE', highlight: true },
                  { aspect: 'Monthly Predictability', traditional: 'Variable', go: 'Fixed per user' },
                  { aspect: 'Scalability', traditional: 'Project each time', go: 'Add users anytime' },
                  { aspect: 'Support Model', traditional: 'Pay per incident', go: 'Unlimited included', highlight: true }
                ].map((row, i) => (
                  <tr key={i} className={row.highlight ? 'bg-green-50' : ''}>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.aspect}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">{row.traditional}</td>
                    <td className={`px-6 py-4 text-center text-sm font-bold ${row.highlight ? 'text-green-700 bg-green-50' : 'text-blue-700'}`}>{row.go}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Zero Implementation Fee Impact */}
      <section className="py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Zero Implementation Fees Changes Everything</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-white rounded-2xl border-2 border-red-200 shadow-lg text-center">
              <h3 className="text-2xl font-bold text-red-700 mb-6">Traditional ERP Project</h3>
              <div className="space-y-4">
                <div className="flex justify-between"><span className="text-gray-700">Software licences:</span><span className="font-bold text-gray-900">£15,000</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Implementation fees:</span><span className="font-bold text-gray-900">£50,000</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Cloud setup:</span><span className="font-bold text-gray-900">£8,000</span></div>
                <div className="flex justify-between border-t-2 border-gray-200 pt-4"><span className="text-gray-700 font-bold">Total upfront:</span><span className="font-bold text-red-700 text-xl">£73,000</span></div>
              </div>
              <p className="text-sm text-gray-500 mt-4 text-center">Plus monthly SAP fees and support costs</p>
            </div>
            
            <div className="p-8 bg-white rounded-2xl border-2 border-green-200 shadow-lg text-center">
              <h3 className="text-2xl font-bold text-green-700 mb-6">Ascarii Go</h3>
              <div className="space-y-4">
                <div className="flex justify-between"><span className="text-gray-700">Software licences:</span><span className="font-bold text-green-700">Included</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Implementation fees:</span><span className="font-bold text-green-700">£0</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Cloud setup:</span><span className="font-bold text-green-700">£0</span></div>
                <div className="flex justify-between border-t-2 border-gray-200 pt-4"><span className="text-gray-700 font-bold">Total upfront:</span><span className="font-bold text-green-700 text-xl">£0</span></div>
              </div>
              <p className="text-sm text-gray-600 mt-4 text-center">Pure subscription from £105/user/month</p>
            </div>
          </div>
          <div className="mt-8 p-8 bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl text-white text-center">
            <p className="text-xl font-semibold">Start small. Scale as you grow. Invest as you succeed. That's the power of pure subscription.</p>
          </div>
        </div>
      </section>

      {/* Everything Included - WITH INTEGRATED FEATURES TABLE */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900" id="included">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything Included - One Simple Price Per User</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">This is the real power of Ascarii Go: SAP Business One, professional services, cloud hosting, support, upgrades, and extensions - all wrapped into one subscription.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Users, title: "Discovery & Design", strapline: "Understand your needs", desc: "Lightweight workshops to align scope and configure your perfect solution" },
              { icon: Zap, title: "Template Implementation", strapline: "Rapid deployment", desc: "Best-practice setup gets you operational in weeks, not months" },
              { icon: Database, title: "Data Migration", strapline: "Safe and complete", desc: "Move products, customers, pricing, and history with confidence" },
              { icon: Cloud, title: "AWS Cloud Hosting", strapline: "99.995% uptime", desc: "Enterprise infrastructure with 24×7 monitoring included" },
              { icon: HeadphonesIcon, title: "Unlimited Support", strapline: "Always available", desc: "Business support 8am-6pm Mon-Fri, plus evenings and weekends for high priority cases. Hosting support 24×7." },
              { icon: RefreshCw, title: "Regular Upgrades", strapline: "Stay current", desc: "1-2 SAP B1 upgrades yearly with 15-day test environments" },
              { icon: Sparkles, title: "Extension Access", strapline: "Grow without limits", desc: "Add manufacturing, warehouse, e-commerce extensions (Pro tier)" },
              { icon: Target, title: "Account Manager", strapline: "Your dedicated contact", desc: "Single point who knows your business and priorities" }
            ].map((service, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white mb-1">{service.title}</h3>
                    <p className="text-xs text-cyan-300 font-medium">{service.strapline}</p>
                  </div>
                </div>
                <p className="text-blue-100 text-xs leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Features Comparison Table - INSIDE THIS SECTION */}
          <div className="border-t border-white/20 pt-16 mt-8">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white mb-4">Compare Features</h3>
              <p className="text-xl text-blue-100">What's included in each level</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <thead className="bg-white/20">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Features</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white">Ascarii Go</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-cyan-300">Ascarii Go Pro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {[
                    { feature: 'Minimum User Count', go: '1', pro: '5' },
                    { feature: 'Discovery & Design Workshops', go: true, pro: true },
                    { feature: 'System Implementation', go: true, pro: true },
                    { feature: 'Post Go-Live Support', go: true, pro: true },
                    { feature: 'SAP B1 Databases (extra purchasable)', go: '2', pro: '4', proHighlight: true },
                    { feature: 'Data Migration Service', go: true, pro: true },
                    { feature: 'Standard Add-On Delivery', go: true, pro: true },
                    { feature: 'Advanced Add-on Delivery', go: false, pro: true, proHighlight: true },
                    { feature: 'Cloud Hosting Design & Build', go: true, pro: true },
                    { feature: 'SAP Business One on AWS Hosting', go: true, pro: true },
                    { feature: 'Unlimited Business Support (8am-6pm + evenings/weekends for priority)', go: true, pro: true },
                    { feature: 'Unlimited Hosting Support (24×7)', go: true, pro: true },
                    { feature: 'SAP B1 Upgrades per Year', go: '1', pro: '2', proHighlight: true },
                    { feature: 'Upgrade Testing Environment (15 days)', go: true, pro: true },
                    { feature: 'Performance Guarantee', go: true, pro: true },
                    { feature: 'SAP Training Centre (2026)', go: true, pro: true },
                    { feature: 'Dedicated Account Manager', go: true, pro: true }
                  ].map((row, i) => (
                    <tr key={i} className={row.proHighlight ? 'bg-cyan-400/20' : ''}>
                      <td className="px-6 py-4 text-sm text-white font-medium">{row.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.go === 'boolean' ? (
                          row.go ? <Check className="w-5 h-5 text-cyan-300 mx-auto" /> : <X className="w-5 h-5 text-white/30 mx-auto" />
                        ) : (
                          <span className="text-sm text-white font-semibold">{row.go}</span>
                        )}
                      </td>
                      <td className={`px-6 py-4 text-center ${row.proHighlight ? 'bg-cyan-400/20' : ''}`}>
                        {typeof row.pro === 'boolean' ? (
                          row.pro ? <Check className="w-5 h-5 text-cyan-300 mx-auto" /> : <X className="w-5 h-5 text-white/30 mx-auto" />
                        ) : (
                          <span className="text-sm text-white font-semibold">{row.pro}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Why Ascarii Go - Enhanced */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="why">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Ascarii Go Changes Everything</h2>
            <p className="text-xl text-gray-600">A solution without limits that changes the way we think about business software</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { icon: TrendingUp, title: 'Scales as you grow', strapline: 'Start small, scale up', desc: 'Add users, modules, and extensions as you expand. No renegotiating or project fees.' },
              { icon: Zap, title: 'Rapid time-to-value', strapline: 'Live in weeks', desc: 'Best-practice templates and proven deployment get you productive fast.' },
              { icon: Shield, title: 'Enterprise-grade', strapline: 'AWS reliability', desc: '99.995% uptime, enterprise security, complete UK/EU data sovereignty.' },
              { icon: Award, title: 'Zero implementation fees', strapline: 'True SaaS model', desc: 'No £50k+ upfront barrier. Pure subscription. Invest as you succeed.' }
            ].map((benefit, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{benefit.title}</h3>
                    <p className="text-sm text-blue-600 font-medium">{benefit.strapline}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Hours */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Add Service Hours for Complete Budget Control</h2>
              <p className="text-lg text-gray-600 mb-6">Need additional consulting or project support? Add Service Hour Bundles. Lock in rates from £123/hour (save up to 18%) and spread costs over 12-36 months.</p>
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-bold text-gray-900 mb-1">100 hours over 24 months</p>
                  <p className="text-sm text-gray-600">£13,200 total (£550/month) - Save £1,800 vs PAYG</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-bold text-gray-900 mb-1">200 hours over 24 months</p>
                  <p className="text-sm text-gray-600">£24,600 total (£1,025/month) - Save £5,400 vs PAYG</p>
                </div>
              </div>
              <a href="/landing/ascarii-service-hours#calculator" target="_blank" rel="noopener noreferrer">
                <Button className="bg-blue-600 hover:bg-blue-700">Calculate Your Savings</Button>
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=85" alt="Service Hours" className="rounded-2xl shadow-2xl" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SAP B1 Solutions */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Build Your Perfect Solution</h2>
            <p className="text-xl text-gray-600">SAP Business One + Extensions = Complete Business Solutions</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Factory, solution: "Manufacturing Solution", components: "SAP B1 + Beas/Netronic", outcome: "Shop floor control, quality management, production planning" },
              { icon: Warehouse, solution: "Warehouse Solution", components: "SAP B1 + Produmex WMS", outcome: "Directed workflows, bin accuracy, multi-site orchestration" },
              { icon: ShoppingCart, solution: "E-Commerce Solution", components: "SAP B1 + FocusPoint", outcome: "B2B portal, real-time pricing, customer-specific catalogues" },
              { icon: TruckIcon, solution: "Transport Solution", components: "SAP B1 + Stream", outcome: "Route optimisation, live tracking, electronic POD" },
              { icon: CreditCard, solution: "Counter Solution", components: "SAP B1 + EPoS Now", outcome: "Fast checkout, trade pricing, account sales" },
              { icon: Globe2, solution: "Group Solution", components: "SAP B1 + B1 InterCompany", outcome: "Multi-subsidiary consolidation, intercompany automation" }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{item.solution}</h3>
                    <p className="text-xs text-blue-600 font-medium">{item.components}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-xs leading-relaxed">{item.outcome}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-12 max-w-3xl mx-auto">With Ascarii Go Pro, all extensions are available to add as you need them. Build exactly the solution your business needs.</p>
        </div>
      </section>

      {/* Solutions Philosophy */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Solutions, Not Just ERP</h2>
            <p className="text-xl text-gray-600 mb-8">
              The ERP acronym is outdated thinking. Modern businesses need <strong>solutions</strong> - best-of-breed products that integrate seamlessly to solve specific problems and improve efficiency.
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-8 border-2 border-blue-200">
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">SAP Business One is your foundation</h3>
                <p className="text-gray-700">Finance, sales, purchasing, inventory - the core platform that everything connects to.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Extensions create your solution</h3>
                <p className="text-gray-700">Manufacturing solution = SAP B1 + Beas. Warehouse solution = SAP B1 + Produmex. E-commerce solution = SAP B1 + FocusPoint.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Ascarii Go brings it all together</h3>
                <p className="text-gray-700">One subscription covers SAP B1, cloud hosting, implementation, support, and access to the extension ecosystem. No silos. No vendor juggling. Just solutions that work.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ - Enhanced */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="faq">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'What is Ascarii Go?', a: 'Ascarii Go is SAP Business One on AWS delivered as a pure subscription with zero implementation fees. Everything you need - software, hosting, implementation, support, upgrades - in one predictable monthly price per user.' },
              { q: 'How is this different from traditional ERP projects?', a: "We eliminate the £50k-£100k upfront barrier. No massive services fees, no long painful projects, no vendor lock-in. You get SAP Business One operational in weeks with template-driven implementation, then scale as you grow. It's true SaaS." },
              { q: 'Which business functions are included?', a: 'Core SAP Business One includes Finance, Sales, Purchasing, Inventory, Production, Service, CRM/SRM and Reporting. With Ascarii Go Pro, you also get access to 12+ extensions for manufacturing, warehouse, e-commerce, transport, and automation.' },
              { q: 'Is my data secure?', a: 'Yes. SAP Business One runs on AWS with 99.995% uptime, enterprise-grade security, UK/EU data sovereignty, automated backups, and disaster recovery. Your data is as secure as the world\'s largest enterprises.' },
              { q: 'Can I try before committing?', a: 'Yes! We provide a free 30-day test environment so you can validate SAP Business One with your actual processes before going live. This is part of the Ascarii Experience Promise.' },
              { q: 'What if I need to add users or change modules?', a: 'Simply notify us. Add users anytime for the per-user monthly rate. Change modules as your business needs evolve. No contracts to renegotiate. That\'s the flexibility of true subscription.' }
            ].map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Form */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="demo">
        <div className="container mx-auto px-4 lg:px-8 max-w-2xl">
          {submitted ? (
            <div className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
              <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-gray-600">Our team will reach out shortly to arrange your Ascarii Go demo.</p>
            </div>
          ) : (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">See Ascarii Go in Action</h2>
                <p className="text-xl text-gray-600">Leave your details and our team will reach out shortly</p>
              </div>
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                  <input type="text" required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                </div>
                <div>
                  <label className="flex items-start gap-3">
                    <input type="checkbox" required checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} className="mt-1 w-5 h-5 text-blue-600 rounded" />
                    <span className="text-sm text-gray-700">I agree to Ascarii contacting me and have read the <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. *</span>
                  </label>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                  {loading ? 'Sending...' : 'Request a Demo'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
      {/* Final CTA - Enhanced */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Transform with Ascarii Go?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join SMEs running their entire business on SAP Business One - with zero implementation fees, complete solutions, and everything included in one simple subscription.
            </p>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-12 border border-white/20">
              <p className="text-xl text-white font-semibold mb-4">When you choose Ascarii Go, you get:</p>
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
                {[
                  "Zero implementation fees (save £50k+)",
                  "Free 30-day test environment",
                  "Free cloud hosting design & build",
                  "All SAP B1 modules included",
                  "Unlimited support (8am-6pm + 24×7 for priority)",
                  "Access to 12+ extension solutions"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <a href="#demo" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-10 py-5 text-xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Book Your Demo
              </a>
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Talk to an Expert
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">SAP Premier Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">AWS Advanced Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">15+ Years Experience</span></div>
            </div>
          </div>
        </div>
      </section>


export default AscariiGoLanding;
